import React, { useState } from 'react';
import { 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Pause, 
  Play, 
  Eye,
  Settings,
  DollarSign,
  BarChart3,
  Mail,
  Download,
  Copy,
  AlertTriangle
} from 'lucide-react';

interface TenantActionsProps {
  tenantId: string;
  tenantName: string;
  status: string;
  onEdit?: (tenantId: string) => void;
  onView?: (tenantId: string) => void;
  onDelete?: (tenantId: string) => void;
  onSuspend?: (tenantId: string) => void;
  onReactivate?: (tenantId: string) => void;
  onChangePlan?: (tenantId: string) => void;
  onViewAnalytics?: (tenantId: string) => void;
  onManage?: (tenantId: string) => void;
}

interface BulkActionsProps {
  selectedTenants: string[];
  onBulkAction: (action: string, tenantIds: string[]) => void;
  onClearSelection: () => void;
}

export const TenantActions: React.FC<TenantActionsProps> = ({
  tenantId,
  tenantName,
  status,
  onEdit,
  onView,
  onDelete,
  onSuspend,
  onReactivate,
  onChangePlan,
  onViewAnalytics,
  onManage
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const normalizedStatus = status?.toLowerCase();
  const isSuspended = normalizedStatus === 'suspended';

  const handleAction = (action: () => void) => {
    action();
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="p-1 text-gray-400 hover:text-gray-600 rounded"
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>

      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Dropdown Menu */}
          <div className="absolute right-0 top-8 z-20 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
            <div className="px-3 py-2 border-b border-gray-100">
              <p className="text-xs font-medium text-gray-500 truncate">{tenantName}</p>
            </div>
            
            {onView && (
              <button
                onClick={() => handleAction(() => onView(tenantId))}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Details
              </button>
            )}
            
            {onEdit && (
              <button
                onClick={() => handleAction(() => onEdit(tenantId))}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Firm
              </button>
            )}
            
            {onViewAnalytics && (
              <button
                onClick={() => handleAction(() => onViewAnalytics(tenantId))}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                View Analytics
              </button>
            )}
            
            <div className="border-t border-gray-100 my-1" />
            
            {onChangePlan && (
              <button
                onClick={() => handleAction(() => onChangePlan(tenantId))}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Change Plan
              </button>
            )}
            
            {onManage && (
              <button
                onClick={() => handleAction(() => onManage(tenantId))}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
              >
                <Settings className="w-4 h-4 mr-2" />
                Manage Firm
              </button>
            )}
            
            <button
              onClick={() => handleAction(() => console.log('Send email to', tenantId))}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
            >
              <Mail className="w-4 h-4 mr-2" />
              Send Email
            </button>
            
            <div className="border-t border-gray-100 my-1" />
            
            {isSuspended ? (
              onReactivate && (
                <button
                  onClick={() => handleAction(() => onReactivate(tenantId))}
                  className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50 flex items-center"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Reactivate
                </button>
              )
            ) : (
              onSuspend && (
                <button
                  onClick={() => handleAction(() => onSuspend(tenantId))}
                  className="w-full text-left px-3 py-2 text-sm text-yellow-700 hover:bg-yellow-50 flex items-center"
                >
                  <Pause className="w-4 h-4 mr-2" />
                  Suspend
                </button>
              )
            )}
            
            {onDelete && (
              <button
                onClick={() => handleAction(() => onDelete(tenantId))}
                className="w-full text-left px-3 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export const BulkActions: React.FC<BulkActionsProps> = ({
  selectedTenants,
  onBulkAction,
  onClearSelection
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<string | null>(null);

  const handleBulkAction = (action: string) => {
    if (['suspend', 'delete'].includes(action)) {
      setShowConfirmDialog(action);
    } else {
      onBulkAction(action, selectedTenants);
      setShowDropdown(false);
    }
  };

  const confirmBulkAction = (action: string) => {
    onBulkAction(action, selectedTenants);
    setShowConfirmDialog(null);
    setShowDropdown(false);
  };

  if (selectedTenants.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex items-center space-x-4 bg-primary-50 border border-primary-200 rounded-lg px-4 py-2">
        <span className="text-sm font-medium text-primary-700">
          {selectedTenants.length} tenant{selectedTenants.length > 1 ? 's' : ''} selected
        </span>
        
        <div className="flex items-center space-x-2">
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="btn-primary text-sm"
            >
              Bulk Actions
            </button>

            {showDropdown && (
              <>
                {/* Backdrop */}
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowDropdown(false)}
                />
                
                {/* Dropdown Menu */}
                <div className="absolute left-0 top-10 z-20 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                  <button
                    onClick={() => handleBulkAction('export')}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Data
                  </button>
                  
                  <button
                    onClick={() => handleBulkAction('change-plan')}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Change Plan
                  </button>
                  
                  <button
                    onClick={() => handleBulkAction('send-email')}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </button>
                  
                  <div className="border-t border-gray-100 my-1" />
                  
                  <button
                    onClick={() => handleBulkAction('suspend')}
                    className="w-full text-left px-3 py-2 text-sm text-yellow-700 hover:bg-yellow-50 flex items-center"
                  >
                    <Pause className="w-4 h-4 mr-2" />
                    Suspend Tenants
                  </button>
                  
                  <button
                    onClick={() => handleBulkAction('reactivate')}
                    className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50 flex items-center"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Reactivate Tenants
                  </button>
                  
                  <button
                    onClick={() => handleBulkAction('delete')}
                    className="w-full text-left px-3 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Tenants
                  </button>
                </div>
              </>
            )}
          </div>
          
          <button
            onClick={onClearSelection}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Clear Selection
          </button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Confirm Bulk Action
                </h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to {showConfirmDialog} {selectedTenants.length} tenant{selectedTenants.length > 1 ? 's' : ''}? 
                This action cannot be undone.
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmDialog(null)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmBulkAction(showConfirmDialog)}
                  className={`btn-${showConfirmDialog === 'delete' ? 'danger' : 'warning'}`}
                >
                  {showConfirmDialog === 'delete' ? 'Delete' : 'Suspend'} Tenants
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Quick action buttons for individual tenants
export const QuickActions: React.FC<{
  tenantId: string;
  status: string;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onSuspend?: (id: string) => void;
  onReactivate?: (id: string) => void;
}> = ({ tenantId, status, onView, onEdit, onSuspend, onReactivate }) => {
  return (
    <div className="flex items-center space-x-1">
      {onView && (
        <button
          onClick={() => onView(tenantId)}
          className="p-1 text-gray-400 hover:text-blue-600 rounded"
          title="View Details"
        >
          <Eye className="w-4 h-4" />
        </button>
      )}
      
      {onEdit && (
        <button
          onClick={() => onEdit(tenantId)}
          className="p-1 text-gray-400 hover:text-green-600 rounded"
          title="Edit Firm"
        >
          <Edit className="w-4 h-4" />
        </button>
      )}
      
      {status === 'SUSPENDED' ? (
        onReactivate && (
          <button
            onClick={() => onReactivate(tenantId)}
            className="p-1 text-gray-400 hover:text-green-600 rounded"
            title="Reactivate"
          >
            <Play className="w-4 h-4" />
          </button>
        )
      ) : (
        onSuspend && (
          <button
            onClick={() => onSuspend(tenantId)}
            className="p-1 text-gray-400 hover:text-yellow-600 rounded"
            title="Suspend"
          >
            <Pause className="w-4 h-4" />
          </button>
        )
      )}
    </div>
  );
};
